"use client";

import React, { useState, useEffect, useMemo } from "react";
import Image from "next/image";
import { motion, AnimatePresence } from "framer-motion";
import styles from "./about.module.css";
import { useFetchEcompage } from "@/apis/queryFunctions/ecompagesFunctions";
import appendImageUrlFromAPI from "@/utils/appendImageUrlFromAPI";
import ActionButton from "@/components/Common/ActionButton";
import { Title, Description } from "@/components/Common/Typography";
import PartnerSection from "@/components/Homepage/PartnerSection/PartnerSection";
import TimelineSection from "./components/TimelineSection";
import WhyChooseSection from "./components/WhyChooseSection";
import { useHeader } from "@/context/HeaderLayoutContext";
import { stripHTML } from "@/utils/stripHTML";

interface GioiThieuClientProps {
  initialData: any;
  commonData: any;
  locale: string;
}

export default function GioiThieuClient({
  initialData,
  commonData,
  locale,
}: GioiThieuClientProps) {
  const { data: fetchResult } = useFetchEcompage(
    "trang-gioi-thieu-website-viet-thien",
    locale
  );
  const data = fetchResult || initialData;
  const { setHeaderStyle } = useHeader();

  useEffect(() => {
    setHeaderStyle({ transparent: true });
    return () => setHeaderStyle({ transparent: false });
  }, [setHeaderStyle]);

  // #region Helper Functions (CMS Data Parsing & Section Control - Home Pattern)
  const allAddOn1 = useMemo(() => {
    const list1 =
      Array.isArray(data?.add_on_data) && data.add_on_data.length > 0
        ? data.add_on_data
        : Array.isArray(commonData?.add_on_data)
          ? commonData.add_on_data
          : [];
    return list1;
  }, [data, commonData]);

  const allAddOn2 = useMemo(() => {
    const list2 =
      Array.isArray(data?.add_on_data_2) && data.add_on_data_2.length > 0
        ? data.add_on_data_2
        : Array.isArray(commonData?.add_on_data_2)
          ? commonData.add_on_data_2
          : [];
    return list2;
  }, [data, commonData]);

  const allAddOn3 = useMemo(() => {
    const list3 =
      Array.isArray(data?.add_on_data_3) && data.add_on_data_3.length > 0
        ? data.add_on_data_3
        : Array.isArray(commonData?.add_on_data_3)
          ? commonData.add_on_data_3
          : [];
    return list3;
  }, [data, commonData]);

  const getAddOn1 = (keys: string[]) => {
    const lowerKeys = keys.map((k) => k.toLowerCase());
    return (
      allAddOn1.find((item: any) => {
        const title = (item?.title || "").toLowerCase();
        const summary = (item?.summary || "").toLowerCase();
        return lowerKeys.some(
          (k) => title === k || summary === k || title.includes(k)
        );
      }) || null
    );
  };

  const isSectionShared = (keys: string[], defaultVal = true): boolean => {
    const item = getAddOn1(keys);
    if (!item) return defaultVal;
    return (
      item.shared !== false &&
      item.shared !== "false" &&
      item.shared !== 0 &&
      item.shared !== "0"
    );
  };

  const getSectionIndex = (keys: string[], defaultVal: number): number => {
    const addon = getAddOn1(keys);
    if (!addon) return defaultVal;
    const stt = addon.stt;
    if (stt === undefined || stt === null || stt === "") {
      return defaultVal;
    }
    const val = Number(stt);
    return isNaN(val) ? defaultVal : val;
  };
  // #endregion

  // #region Banner Data Fetch & JSON Parse
  const bannerRecord = getAddOn1(["banner"]);

  const bannerPictures = useMemo(() => {
    if (!bannerRecord) return [];
    return [
      bannerRecord.picture,
      bannerRecord.picture_2,
      bannerRecord.picture_3,
      bannerRecord.picture_4,
      bannerRecord.picture_5,
      bannerRecord.picture_6,
    ].filter(Boolean);
  }, [bannerRecord]);

  const bannerTabs = useMemo(() => {
    if (!bannerRecord) return [];
    try {
      const rawContent = bannerRecord.content || "";
      const cleanContent = stripHTML(rawContent).trim();
      if (cleanContent.startsWith("[") || cleanContent.startsWith("{")) {
        const parsed = JSON.parse(cleanContent);
        if (Array.isArray(parsed) && parsed.length > 0) {
          return parsed.map((item: any, idx: number) => ({
            tab: item.tab || item.title || `Tab ${idx + 1}`,
            title: item.title || "",
            description: item.description || item.content || "",
            picture:
              bannerPictures[idx] ||
              bannerPictures[0] ||
              bannerRecord.picture ||
              "",
          }));
        }
      }
    } catch (error) {
      console.error("Error parsing banner content JSON:", error);
    }

    // Fallback nếu content không phải JSON
    return [
      {
        tab:
          bannerRecord.summary ||
          bannerRecord.title ||
          (locale === "vi" ? "Giới thiệu" : "About Us"),
        title: bannerRecord.title || "",
        description: bannerRecord.content || "",
        picture: bannerPictures[0] || bannerRecord.picture || "",
      },
    ];
  }, [bannerRecord, bannerPictures, locale]);

  const [activeIndex, setActiveIndex] = useState(0);
  const activeTab = bannerTabs[activeIndex] || bannerTabs[0];
  const activeBannerImg =
    activeTab?.picture || bannerPictures[0] || bannerRecord?.picture || "";
  // #endregion

  // #region Partner & Certification Data Fetch & Parse
  const partnerData = getAddOn1(["doi-tac-chung-nhan", "doi-tac"]);

  const partnerHeaders = useMemo(() => {
    let parsed: any = null;
    try {
      const rawContent = partnerData?.content || "";
      const cleanContent = stripHTML(rawContent).trim();
      if (cleanContent.startsWith("{") || cleanContent.startsWith("[")) {
        parsed = JSON.parse(cleanContent);
      }
    } catch (error) {
      console.error("Error parsing partner headers JSON:", error);
    }
    return {
      partners: parsed?.partners || {
        title: locale === "vi" ? "ĐỐI TÁC CỦA CHÚNG TÔI" : "OUR PARTNERS",
        desc:
          locale === "vi"
            ? "Chúng tôi tự hào hợp tác cùng các thương hiệu uy tín hàng đầu Việt Nam và quốc tế."
            : "We are proud to partner with leading reputable brands in Vietnam and internationally.",
      },
      certifications: parsed?.certifications || {
        title:
          locale === "vi" ? "CHỨNG NHẬN CHẤT LƯỢNG" : "QUALITY CERTIFICATION",
        desc:
          locale === "vi"
            ? "Các chứng nhận chất lượng sản phẩm, đảm bảo uy tín và niềm tin cho đối tác."
            : "Product quality certifications, ensuring prestige and trust for partners.",
      },
    };
  }, [partnerData, locale]);

  const partnerLogos = useMemo(() => {
    return allAddOn2
      .filter(
        (item: any) =>
          (item?.title || "").toLowerCase() === "doi-tac" &&
          item?.shared !== false &&
          item?.shared !== "false"
      )
      .flatMap((item: any) => [
        item.picture,
        item.picture_2,
        item.picture_3,
        item.picture_4,
        item.picture_5,
        item.picture_6,
      ])
      .filter(Boolean);
  }, [allAddOn2]);

  const certificationImages = useMemo(() => {
    return allAddOn2
      .filter(
        (item: any) =>
          (item?.title || "").toLowerCase() === "chung-nhan" &&
          item?.shared !== false &&
          item?.shared !== "false"
      )
      .flatMap((item: any) => [
        item.picture,
        item.picture_2,
        item.picture_3,
        item.picture_4,
        item.picture_5,
        item.picture_6,
      ])
      .filter(Boolean);
  }, [allAddOn2]);
  // #endregion

  // #region Timeline & Why Choose Data
  const timelineHeaderRecord = getAddOn1([
    "lich-su-hinh-thanh",
    "lich-su",
    "timeline",
  ]);
  const timelineTitle =
    timelineHeaderRecord?.summary ||
    timelineHeaderRecord?.title ||
    (locale === "vi" ? "Lịch sử hình thành" : "Formation History");
  const timelineSubtitle = stripHTML(
    timelineHeaderRecord?.content || ""
  ).trim();

  const timelineItems = useMemo(() => {
    // 1. From allAddOn2 with title 'lich-su-hinh-thanh' or 'timeline'
    const fromAddOn2 = (allAddOn2 || []).filter(
      (item: any) =>
        ["lich-su-hinh-thanh", "lich-su", "timeline"].includes(
          (item?.title || "").toLowerCase()
        ) &&
        item?.shared !== false &&
        item?.shared !== "false"
    );
    if (fromAddOn2.length > 0) return fromAddOn2;

    // 2. Fallback to allAddOn3
    return (allAddOn3 || []).filter(
      (item: any) => item?.shared !== false && item?.shared !== "false"
    );
  }, [allAddOn2, allAddOn3]);

  // #region Why Choose Data (Lý do chọn Việt Thiên)
  const whyChooseRecord = getAddOn1([
    "ly-do-chon-viet-thien",
    "ly-do",
    "tai-sao",
    "chon-viet-thien",
    "tai-sao-chon-viet-thien",
    "why-choose",
  ]);

  const whyChooseTitle =
    whyChooseRecord?.summary ||
    whyChooseRecord?.title ||
    (locale === "vi" ? "Lý do chọn Việt Thiên" : "Why Choose Viet Thien");

  const whyChoosePictures = useMemo(() => {
    if (!whyChooseRecord) return [];
    return [
      whyChooseRecord.picture,
      whyChooseRecord.picture_2,
      whyChooseRecord.picture_3,
      whyChooseRecord.picture_4,
      whyChooseRecord.picture_5,
      whyChooseRecord.picture_6,
    ].filter(Boolean);
  }, [whyChooseRecord]);

  const whyChooseItems = useMemo(() => {
    // 1. Parse JSON từ whyChooseRecord.content trong allAddOn1
    if (whyChooseRecord?.content) {
      try {
        const rawContent = whyChooseRecord.content || "";
        const cleanContent = stripHTML(rawContent).trim();
        if (cleanContent.startsWith("[") || cleanContent.startsWith("{")) {
          const parsed = JSON.parse(cleanContent);
          if (Array.isArray(parsed) && parsed.length > 0) {
            return parsed.map((item: any, idx: number) => ({
              title: item.title || "",
              description: item.description || item.content || "",
              picture: appendImageUrlFromAPI({
                src: whyChoosePictures[idx] || whyChooseRecord.picture,
                size: "2k",
              }),
            }));
          }
        }
      } catch (err) {
        console.error("Error parsing why choose content JSON:", err);
      }
    }
    return [];
  }, [whyChooseRecord, whyChoosePictures]);

  const chonVietThien = useMemo(() => {
    return (allAddOn2 || []).filter(
      (item: any) =>
        [
          "tai-sao",
          "chon-viet-thien",
          "tai-sao-chon-viet-thien",
          "vi-sao",
          "ly-do-chon-viet-thien",
          "ly-do",
        ].includes((item?.title || "").toLowerCase()) &&
        item?.shared !== false &&
        item?.shared !== "false"
    );
  }, [allAddOn2]);
  // #endregion

  // #region Dynamic Sections List (Sorted by STT & Shared)
  const sections = useMemo(() => {
    const list: {
      id: string;
      index: number;
      render: () => React.ReactNode;
    }[] = [];

    // 1. BANNER SECTION
    if (isSectionShared(["banner"], true)) {
      list.push({
        id: "banner",
        index: getSectionIndex(["banner"], 1),
        render: () => (
          <section key="banner" className={styles.bannerSection}>
            {activeBannerImg && (
              <Image
                src={appendImageUrlFromAPI({
                  src: activeBannerImg,
                  size: "2k",
                })}
                alt={activeTab?.title || "Banner Giới thiệu"}
                fill
                priority
                sizes="100vw"
                className={styles.bannerBgImage}
              />
            )}

            <div className={styles.bannerContent}>
              {/* Active Content Left-aligned (85% width) */}
              <div className={styles.textBlock}>
                <AnimatePresence mode="wait">
                  <motion.div
                    key={`banner-content-${activeIndex}`}
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.35, ease: "easeOut" }}
                  >
                    {activeTab?.title && (
                      <Title
                        size="medium"
                        as="h1"
                        color="#ffffff"
                        align="left"
                        style={{
                          marginBottom: "14px",
                          textShadow: "0 2px 10px rgba(0, 0, 0, 0.45)",
                        }}
                      >
                        {activeTab.title}
                      </Title>
                    )}

                    {activeTab?.description && (
                      <Description
                        as="div"
                        size="medium"
                        color="rgba(255, 255, 255, 0.95)"
                        align="left"
                        className={styles.bannerDesc}
                        style={{
                          textShadow: "0 1px 5px rgba(0, 0, 0, 0.35)",
                          lineHeight: 1.7,
                        }}
                        dangerouslySetInnerHTML={{
                          __html: activeTab.description,
                        }}
                      />
                    )}
                  </motion.div>
                </AnimatePresence>

                {/* Nav Buttons below text using ActionButton */}
                {bannerTabs.length > 1 && (
                  <div className={styles.navGroup}>
                    {bannerTabs.map((item: any, index: number) => {
                      const isActive = activeIndex === index;
                      return (
                        <ActionButton
                          key={index}
                          label={item.tab}
                          onClick={() => setActiveIndex(index)}
                          color={
                            isActive ? "#008748" : "rgba(255, 255, 255, 0.2)"
                          }
                          hoverColor="#008748"
                          showIcon={false}
                          style={{
                            padding: "8px 20px",
                            fontSize: "13px",
                            fontWeight: 600,
                            border: isActive
                              ? "1px solid #008748"
                              : "1px solid rgba(255, 255, 255, 0.4)",
                            backdropFilter: "blur(8px)",
                            WebkitBackdropFilter: "blur(8px)",
                          }}
                        />
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </section>
        ),
      });
    }

    // 2. PARTNERS & CERTIFICATES SECTION
    if (isSectionShared(["doi-tac-chung-nhan", "doi-tac"], true)) {
      list.push({
        id: "partners",
        index: getSectionIndex(["doi-tac-chung-nhan", "doi-tac"], 2),
        render: () => (
          <div key="partners" className="pt-24 pb-20">
            <PartnerSection
              isActive={true}
              partnerLogos={partnerLogos}
              certificationImages={certificationImages}
              partnerHeader={partnerHeaders.partners}
              certHeader={partnerHeaders.certifications}
              locale={locale}
            />
          </div>
        ),
      });
    }

    // 3. TIMELINE SECTION (Lịch sử hình thành)
    if (
      isSectionShared(["lich-su-hinh-thanh", "timeline", "lich-su"], true) &&
      timelineItems.length > 0
    ) {
      list.push({
        id: "timeline",
        index: getSectionIndex(
          ["lich-su-hinh-thanh", "timeline", "lich-su"],
          3
        ),
        render: () => (
          <TimelineSection
            key="timeline"
            apiData={timelineItems}
            title={timelineTitle}
            subtitle={timelineSubtitle}
          />
        ),
      });
    }

    // 4. WHY CHOOSE SECTION
    const isWhyChooseShared =
      (whyChooseRecord &&
        whyChooseRecord.shared !== false &&
        whyChooseRecord.shared !== "false") ||
      isSectionShared(
        [
          "ly-do-chon-viet-thien",
          "ly-do",
          "tai-sao",
          "chon-viet-thien",
          "tai-sao-chon-viet-thien",
        ],
        true
      );

    if (
      isWhyChooseShared &&
      (whyChooseItems.length > 0 || chonVietThien.length > 0)
    ) {
      list.push({
        id: "why-choose",
        index: getSectionIndex(
          [
            "ly-do-chon-viet-thien",
            "ly-do",
            "tai-sao",
            "chon-viet-thien",
            "tai-sao-chon-viet-thien",
          ],
          whyChooseRecord?.stt !== undefined && whyChooseRecord?.stt !== null
            ? Number(whyChooseRecord.stt)
            : 4
        ),
        render: () => (
          <WhyChooseSection
            key="why-choose"
            title={whyChooseTitle}
            items={whyChooseItems}
            chonVietThien={chonVietThien}
          />
        ),
      });
    }

    return list.sort((a, b) => a.index - b.index);
  }, [
    allAddOn1,
    allAddOn2,
    allAddOn3,
    activeBannerImg,
    activeTab,
    activeIndex,
    bannerTabs,
    partnerLogos,
    certificationImages,
    partnerHeaders,
    timelineItems,
    timelineTitle,
    timelineSubtitle,
    whyChooseRecord,
    whyChooseTitle,
    whyChooseItems,
    chonVietThien,
    locale,
  ]);
  // #endregion

  if (!data) return null;

  return (
    <div className={styles.section}>
      {sections.map((section) => (
        <React.Fragment key={section.id}>{section.render()}</React.Fragment>
      ))}
    </div>
  );
}
